import React, { useEffect, useState } from 'react';
import Container from 'src/components/shared/Container';
import { Spinner } from 'flowbite-react';
import { Zoom } from '@mui/material';
import {
	ApplicationStatus,
	CampusUserWithApplication,
	GetPositionsForRecruiterAPIResponse,
	TypeOfUsers,
	UserExperienceLevelType,
} from 'types';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CandidateFilters from '../campus/campusCandidateListing/CandidateFilters';
import CandidateList from '../campus/campusCandidateListing/CandidateList';
import PositionCandidatesHeader from './PositionCandidatesHeader';
import { QueryState } from 'react-query/types/core/query';
import { useQueryClient } from 'react-query';
import {
	useGetHiringApplicantsForPosition,
	useGetHiringCandidatesForPosition,
	useGetRecommendationsForPositionQuery,
} from 'src/hooks/position.hooks';
import PositionCandidateListingTabs, { PositionCandidatesTabsEnum } from './PositionCandidateListingTabs';
import ViewAssignment from 'src/components/my-applications/assignment/ViewAssignment';
import ViewOffer from 'src/components/my-applications/offer/ViewOffer';
import NewCreatingPositionLoader from 'src/components/position/NewCreatingPositionLoader';

const PositionCandidateListing = () => {
	const navigate = useNavigate();
	const { positionId } = useParams();
	const [page, setPage] = useState(0);
	const queryClient = useQueryClient();
	const [rowsPerPage, setRowsPerPage] = useState(5);
	const [searchParams, setSearchParams] = useSearchParams();
	const search = searchParams.get('search') || '';
	const defaultTab = searchParams.get('tab') || '';
	const viewOffer = searchParams.get('viewOffer');
	const viewAssignment = searchParams.get('viewAssignment');
	const getMyPositionsData: QueryState<GetPositionsForRecruiterAPIResponse, undefined> | undefined =
		queryClient.getQueryState(['getMyRegisteredPositions', '']);
	const [tab, setTab] = useState(
		(defaultTab as PositionCandidatesTabsEnum) || PositionCandidatesTabsEnum.RECOMMENDATIONS,
	);
	const [bulkSelectedCandidates, setBulkSelectedCandidates] = React.useState<string[]>([]);
	const [inPipelineUsers, setInPipelineUsers] = useState<CampusUserWithApplication[]>([]);

	const { data, isLoading } = useGetHiringCandidatesForPosition({
		page,
		positionId: positionId || '',
		limit: rowsPerPage,
		filters: {
			search: searchParams.get('search') || '',
			sortBy: searchParams.get('sortBy') || 'bestMatch',
			roles: JSON.parse(searchParams.get('role') || '[]'),
			skills: JSON.parse(searchParams.get('skills') || '[]'),
			salary: JSON.parse(searchParams.get('salary') || '[]'),
			experienceLevelBuckets:
				getMyPositionsData?.data?.positions?.find(e => e?.position?._id?.toString() === positionId)?.position
					?.positionExperienceBuckets || [],
			locations: JSON.parse(searchParams.get('location') || '[]'),
			expectedJoining: JSON.parse(searchParams.get('availability') || '[]'),
		},
	});

	const {
		data: applicationsData,
		isLoading: applicationLoading,
		isFetching: applicationFetching,
	} = useGetHiringApplicantsForPosition({
		page,
		positionId: positionId || '',
		limit: rowsPerPage,
		filters: {
			search: searchParams.get('search') || '',
			// sortBy: searchParams.get('sortBy') || 'bestMatch',
			// roles: JSON.parse(searchParams.get('role') || '[]'),
			// skills: JSON.parse(searchParams.get('skills') || '[]'),
			// salary: JSON.parse(searchParams.get('salary') || '[]'),
			// experienceLevelBuckets: [UserExperienceLevelType.FRESHER],
			// locations: JSON.parse(searchParams.get('location') || '[]'),
			// expectedJoining: JSON.parse(searchParams.get('availability') || '[]'),
		},
	});

	const totalShortlistedCandidates =
		applicationsData?.campusHiringCandidates?.filter(
			e =>
				e?.application?.history?.find(h => h?.newStatus === ApplicationStatus.SHORTLISTED) ||
				e?.application?.status === ApplicationStatus.SHORTLISTED,
		)?.length || 0;

	useEffect(() => {
		if (positionId && !getMyPositionsData?.isFetching) {
			if (!getMyPositionsData?.data?.positions?.find(e => e?.position?._id?.toString() === positionId)) {
				navigate('/');
			}
		}
	}, [getMyPositionsData, positionId]);

	const {
		data: positionRecomendationData,
		isLoading: isPositionRecomendationDataLoading,
		isFetching: isFetchingRecommendations,
	} = useGetRecommendationsForPositionQuery({
		positionId: positionId ?? '',
		search: searchParams.get('search') || '',
	});

	useEffect(() => {
		if (positionRecomendationData?.inPipeLineUsers?.length && applicationsData?.campusHiringCandidates?.length) {
			const users: CampusUserWithApplication[] = [];
			applicationsData?.campusHiringCandidates?.map(s => {
				positionRecomendationData?.inPipeLineUsers?.map((e, i) => {
					if (s._id === e?.user?._id) users.push({ ...s, phone: e?.user?.phone });
				});
			});
			setInPipelineUsers(users);
		}
	}, [positionRecomendationData, applicationsData, positionId]);

	if (viewAssignment) {
		const connectedUser = positionRecomendationData?.connectedUsers?.find(
			r => r.application?.ongoingRound?._id && String(r.application?.ongoingRound?._id) === viewAssignment,
		);
		if (connectedUser?.application) {
			return (
				<div className="max-w-[1440px] mx-auto px-4 lg:px-[80px] w-full">
					<ViewAssignment
						application={connectedUser?.application}
						applicationUser={connectedUser?.user}
						positionId={positionId}
					/>
				</div>
			);
		}
	} else if (viewOffer) {
		const application = positionRecomendationData?.connectedUsers?.find(
			r => r.application?._id && String(r.application?._id) === viewOffer,
		)?.application;
		if (application) {
			const jobSeekerUserId = application?.user?._id || application?.user;

			return (
				<div className="py-[10px] max-w-[1440px] mx-auto px-4 lg:px-[80px] w-full">
					<ViewOffer
						positionTitle={positionRecomendationData.position?.title}
						companyName={positionRecomendationData.position?.company.name}
						application={application}
						positionId={positionId}
						positionWorkingMode={positionRecomendationData.position?.workingMode}
						jobSeekerUser={positionRecomendationData.inPipeLineUsers?.find(u => u?.user?._id === jobSeekerUserId)?.user}
					/>
				</div>
			);
		}
	}

	return (
		<Container>
			<div>
				<PositionCandidatesHeader
					onPositionChange={() => {
						setInPipelineUsers([]);
						setTab(PositionCandidatesTabsEnum.RECOMMENDATIONS);
					}}
				/>
			</div>

			<Zoom in={!!totalShortlistedCandidates || !!search} unmountOnExit>
				<div className="mt-[10px] pb-[6px] w-full max-w-[558px]">
					<PositionCandidateListingTabs
						tab={tab}
						setTab={setTab}
						onChange={data => {
							setPage(0);
							setBulkSelectedCandidates([]);
							setSearchParams({ tab: data });
						}}
						isTourActive={false}
						totalConections={totalShortlistedCandidates}
					/>
				</div>
			</Zoom>

			<div className="mt-[10px]">
				<CandidateFilters
					selectedTab={tab}
					resetPage={() => setPage(0)}
					typeOfUsers={TypeOfUsers.FRESHER}
					bulkSelectedCandidates={bulkSelectedCandidates}
					setBulkSelectedCandidates={setBulkSelectedCandidates}
					isApplicationApiLoading={applicationLoading || applicationFetching}
					candidates={
						(tab === PositionCandidatesTabsEnum.RECOMMENDATIONS
							? data?.campusHiringCandidates
							: tab === PositionCandidatesTabsEnum.SHORTLISTED
							  ? applicationsData?.campusHiringCandidates?.filter(
										e =>
											e?.application?.history?.find(h => h?.newStatus === ApplicationStatus.SHORTLISTED) ||
											e?.application?.status === ApplicationStatus.SHORTLISTED,
							    )
							  : tab === PositionCandidatesTabsEnum.CONNECTED
							    ? inPipelineUsers
							    : []) || []
					}
				/>
			</div>
			{isLoading || applicationLoading || isPositionRecomendationDataLoading ? (
				<div className="mt-[22px] flex items-center justify-center h-[50vh]">
					{/* <Spinner /> */}
					<NewCreatingPositionLoader showAnimation={true} progressBar={false} customClassCss="!w-[30%] mx-auto " />
				</div>
			) : (
				<div className="mt-[22px] pb-5">
					<CandidateList
						page={page}
						setPage={setPage}
						selectedTab={tab}
						count={
							tab === PositionCandidatesTabsEnum.RECOMMENDATIONS
								? data?.total || 0
								: tab === PositionCandidatesTabsEnum.SHORTLISTED
								  ? applicationsData?.total || 0
								  : applicationsData?.total
								    ? applicationsData?.total - (applicationsData?.total - inPipelineUsers?.length)
								    : 0
						}
						rowsPerPage={rowsPerPage}
						setRowsPerPage={setRowsPerPage}
						isPositionCreated={!!positionId}
						typeOfUsers={TypeOfUsers.FRESHER}
						bulkSelectedCandidates={bulkSelectedCandidates}
						setBulkSelectedCandidates={setBulkSelectedCandidates}
						candidates={
							(tab === PositionCandidatesTabsEnum.RECOMMENDATIONS
								? data?.campusHiringCandidates
								: tab === PositionCandidatesTabsEnum.SHORTLISTED
								  ? applicationsData?.campusHiringCandidates?.filter(
											e =>
												e?.application?.history?.find(h => h?.newStatus === ApplicationStatus.SHORTLISTED) ||
												e?.application?.status === ApplicationStatus.SHORTLISTED,
								    )
								  : tab === PositionCandidatesTabsEnum.CONNECTED
								    ? inPipelineUsers
								    : []) || []
						}
						totalShortlistedCandidates={totalShortlistedCandidates}
					/>
				</div>
			)}
		</Container>
	);
};

export default PositionCandidateListing;
